import { isInIframe, isUatEnvironment } from '@/utils/environment';

export type CameraAccessErrorCode =
  | 'IFRAME_CAMERA_BLOCKED'
  | 'CAMERA_PERMISSION_DENIED'
  | 'CAMERA_UNAVAILABLE'
  | 'SCAN_INIT_FAILED';

export interface CameraAccessFailure {
  code: CameraAccessErrorCode;
  message: string;
}

export type CameraAccessResult = { ok: true } | ({ ok: false } & CameraAccessFailure);

export const PRODUCTION_SCAN_INTERRUPTED_MESSAGE =
  'The scan could not be completed. Please try again.';

/** Whether Permissions-Policy allows camera in this document (iframe inheritance included). */
export function isCameraPolicyAllowed(): boolean {
  if (typeof document === 'undefined') return true;
  return document.permissionsPolicy?.allowsFeature('camera') ?? true;
}

function getScanOrigin(): string {
  return typeof window !== 'undefined'
    ? window.location.origin
    : 'https://sugar-free-scan-uat.goodflip.in';
}

function iframeBlockedMessageDetailed(): string {
  const origin = getScanOrigin();

  return isInIframe()
    ? `Camera is blocked in this embedded view. The host page must delegate camera to this iframe and include ${origin} in its Permissions-Policy header allowlist — iframe allow="camera" alone is not enough when the parent restricts camera to (self).`
    : 'Camera is blocked by browser permissions policy in this document.';
}

function iframeBlockedMessage(): string {
  return isUatEnvironment()
    ? iframeBlockedMessageDetailed()
    : PRODUCTION_SCAN_INTERRUPTED_MESSAGE;
}

export function getCameraEmbedDiagnostics(): Record<string, string | boolean> {
  const origin = getScanOrigin();

  return {
    code: 'IFRAME_CAMERA_BLOCKED',
    scanOrigin: origin,
    inIframe: isInIframe(),
    cameraPolicyAllowed: isCameraPolicyAllowed(),
    iframeAllow: 'camera *; microphone *',
    parentPermissionsPolicy: `camera=(self ${origin}), microphone=(self ${origin})`,
  };
}

export function logCameraAccessError(error: CameraAccessFailure): void {
  if (isUatEnvironment()) return;

  if (error.code === 'IFRAME_CAMERA_BLOCKED') {
    console.error('[CarePlix Scan] Camera access error', {
      code: error.code,
      message: iframeBlockedMessageDetailed(),
      diagnostics: getCameraEmbedDiagnostics(),
    });
    return;
  }

  console.error('[CarePlix Scan] Camera access error', error);
}

function toAccessFailure(err: unknown): CameraAccessFailure {
  const domErr = err instanceof DOMException ? err : null;
  const message =
    domErr?.message ?? (err instanceof Error ? err.message : 'Unable to access the camera.');
  const combined = `${domErr?.name ?? ''} ${message}`.toLowerCase();

  if (
    combined.includes('permissions policy') ||
    combined.includes('not allowed in this document') ||
    !isCameraPolicyAllowed() ||
    (isInIframe() &&
      domErr?.name === 'NotAllowedError' &&
      !combined.includes('permission dismissed'))
  ) {
    return {
      code: 'IFRAME_CAMERA_BLOCKED',
      message: iframeBlockedMessage(),
    };
  }

  if (domErr?.name === 'NotAllowedError' || domErr?.name === 'PermissionDeniedError') {
    return {
      code: 'CAMERA_PERMISSION_DENIED',
      message: 'Camera permission was denied. Allow camera access and try again.',
    };
  }

  return {
    code: 'CAMERA_UNAVAILABLE',
    message: message || 'Unable to access the camera.',
  };
}

/** Probes camera access and immediately releases the stream. */
export async function probeCameraAccess(): Promise<CameraAccessResult> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      code: 'CAMERA_UNAVAILABLE',
      message: 'Camera is not supported in this browser.',
    };
  }

  if (!isCameraPolicyAllowed()) {
    return {
      ok: false,
      code: 'IFRAME_CAMERA_BLOCKED',
      message: iframeBlockedMessage(),
    };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user' },
      audio: false,
    });
    stream.getTracks().forEach((track) => track.stop());
    return { ok: true };
  } catch (err) {
    return { ok: false, ...toAccessFailure(err) };
  }
}

export function mapScanStartError(err: unknown): CameraAccessFailure {
  const message = err instanceof Error ? err.message : String(err);
  const lower = message.toLowerCase();

  if (
    lower.includes('permissions policy') ||
    lower.includes('not allowed in this document') ||
    lower.includes('facescan initialization') ||
    !isCameraPolicyAllowed()
  ) {
    return toAccessFailure(err);
  }

  if (lower.includes('camera')) {
    return toAccessFailure(err);
  }

  return {
    code: 'SCAN_INIT_FAILED',
    message: message || 'Failed to start the face scan.',
  };
}

export function openScanInNewTab(): void {
  window.open(window.location.href, '_blank', 'noopener,noreferrer');
}
